// src/utils/storage.js
// Capacitor-aware async storage adapter for the Supabase auth session.
//
// On native iOS/Android the session tokens are stored via @capacitor/preferences
// (iOS: UserDefaults in the app's sandboxed container; Android: SharedPreferences),
// which is the store Supabase recommends for Capacitor. This keeps the tokens out
// of the WKWebView localStorage store and out of the web/JS-reachable surface, and
// lets the background runner (CapacitorKV, same UserDefaults backing) read them.
//
// On web it uses localStorage (unchanged).
//
// NOTE: UserDefaults/SharedPreferences are sandbox-protected but not the iOS
// Keychain — for backup-exclusion and passcode-tied encryption of the refresh
// token specifically, a dedicated Keychain plugin would be a further hardening.
//
// Robustness: a prior build disabled Preferences after a launch crash. This
// adapter loads the plugin lazily (never at module-eval time) and, on ANY error,
// permanently falls back to localStorage for the rest of the session — so a
// misbehaving plugin degrades gracefully instead of crashing the app. Existing
// tokens written to localStorage by the previous build are migrated on first read.
//
// Implements the interface expected by @supabase/supabase-js:
//   getItem(key)        → Promise<string | null>
//   setItem(key, value) → Promise<void>
//   removeItem(key)     → Promise<void>

import { Capacitor } from "@capacitor/core";

const isNative = (() => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
})();

// Once Preferences fails for any reason we stop trying it for this session.
let preferencesDisabled = !isNative;
let _prefsPromise = null;

function localGet(key)  { try { return localStorage.getItem(key); } catch { return null; } }
function localSet(k, v) { try { localStorage.setItem(k, v); } catch {} }
function localDel(key)  { try { localStorage.removeItem(key); } catch {} }

async function getPreferences() {
  if (preferencesDisabled) return null;
  if (!_prefsPromise) {
    const timeout = new Promise((resolve) =>
      setTimeout(() => {
        console.warn("[storage] @capacitor/preferences timed out, falling back to localStorage");
        preferencesDisabled = true;
        resolve(null);
      }, 2000)
    );
    _prefsPromise = Promise.race([
      import("@capacitor/preferences")
        .then((m) => m.Preferences)
        .catch((err) => {
          console.warn("[storage] @capacitor/preferences unavailable, using localStorage:", err?.message);
          preferencesDisabled = true;
          return null;
        }),
      timeout,
    ]);
  }
  return _prefsPromise;
}

export const storage = {
  async getItem(key) {
    const prefs = await getPreferences();
    if (!prefs) return localGet(key);
    try {
      const { value } = await prefs.get({ key });
      if (value != null) return value;
      // Migration: a value written by the previous localStorage-only build.
      const legacy = localGet(key);
      if (legacy != null) {
        try { await prefs.set({ key, value: legacy }); localDel(key); } catch {}
        return legacy;
      }
      return null;
    } catch (err) {
      console.warn("[storage] Preferences.get failed, falling back to localStorage:", err?.message);
      preferencesDisabled = true;
      return localGet(key);
    }
  },

  async setItem(key, value) {
    const prefs = await getPreferences();
    if (!prefs) { localSet(key, value); return; }
    try {
      await prefs.set({ key, value });
    } catch (err) {
      console.warn("[storage] Preferences.set failed, falling back to localStorage:", err?.message);
      preferencesDisabled = true;
      localSet(key, value);
    }
  },

  async removeItem(key) {
    const prefs = await getPreferences();
    if (!prefs) { localDel(key); return; }
    try {
      await prefs.remove({ key });
    } catch (err) {
      console.warn("[storage] Preferences.remove failed, falling back to localStorage:", err?.message);
      preferencesDisabled = true;
      localDel(key);
    }
    // Always clear any legacy localStorage copy too.
    localDel(key);
  },
};
